import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import DirectorsCommandView from '../components/DirectorsCommandView';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { API_BASE } from '../config/api';

/**
 * WorkspaceListPage — `/workspaces`
 * Renders the "Director's Command" view: hero + workspace cards + films list.
 * For this page we fetch films *per workspace* so we get the scene/shot counts
 * needed to derive each film's status.
 */
const WorkspaceListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaces, loading: wsLoading, deleteWorkspace } = useWorkspace();

  const [films, setFilms] = useState([]);
  const [filmsLoading, setFilmsLoading] = useState(false);
  const [pendingDeleteWs, setPendingDeleteWs] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch films per workspace and merge — gives us scene/shot/covered counts.
  const fetchAllFilms = useCallback(async (wsList) => {
    if (!wsList || wsList.length === 0) {
      setFilms([]);
      return;
    }
    setFilmsLoading(true);
    try {
      const results = await Promise.all(
        wsList.map(async (ws) => {
          try {
            const res = await axios.get(`${API_BASE}/workspaces/${ws.id}/films`, { headers: getAuthHeaders() });
            return (res.data || []).map((f) => ({
              ...f,
              workspace_id: f.workspace_id || ws.id,
              workspace_name: ws.name,
            }));
          } catch (e) {
            // soft-fail per workspace so a single broken WS doesn't kill the whole list
            console.warn(`films fetch failed for ws=${ws.id}`, e?.response?.status);
            return [];
          }
        })
      );
      const merged = results.flat();
      // newest first by created_at
      merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setFilms(merged);
    } finally {
      setFilmsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!wsLoading) {
      fetchAllFilms(workspaces);
    }
  }, [wsLoading, workspaces, fetchAllFilms]);

  const handleDeleteRequest = (ws) => setPendingDeleteWs(ws);
  const handleDeleteConfirm = async () => {
    if (!pendingDeleteWs) return;
    setDeleting(true);
    try {
      await deleteWorkspace(pendingDeleteWs.id);
      toast.success(`Deleted ${pendingDeleteWs.name}`);
      setPendingDeleteWs(null);
    } catch (e) {
      toast.error(e?.message || 'Failed to delete workspace');
    } finally {
      setDeleting(false);
    }
  };

  const sortedWorkspaces = useMemo(() => {
    // owned first, then by name
    return [...workspaces].sort((a, b) => {
      const aOwn = (a.role === 'owner' || a.owner_id === user?.id) ? 0 : 1;
      const bOwn = (b.role === 'owner' || b.owner_id === user?.id) ? 0 : 1;
      if (aOwn !== bOwn) return aOwn - bOwn;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [workspaces, user?.id]);

  return (
    <div className="bg-background" data-testid="workspace-list-page">
      <DirectorsCommandView
        workspaces={sortedWorkspaces}
        films={films}
        currentUserId={user?.id}
        loading={wsLoading}
        filmsLoading={filmsLoading}
        showCreateFilmCta={false}
        onCreateWorkspace={() => navigate('/workspace/purchase')}
        onDeleteWorkspace={handleDeleteRequest}
      />

      <AlertDialog open={!!pendingDeleteWs} onOpenChange={(open) => { if (!open) setPendingDeleteWs(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-semibold">{pendingDeleteWs?.name}</span>{' '}
              and all of its films, scenes, and shots. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-workspace"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkspaceListPage;
